import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PlanCard from './components/PlanCard';
import UsageMetrics from './components/UsageMetrics';
import InvoiceTable from './components/InvoiceTable';
import PaymentMethod from './components/PaymentMethod';
import UpgradeCard from './components/UpgradeCard';
import BillingAlerts from './components/BillingAlerts';
import PlanManagementModal from './components/PlanManagementModal';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { Plus, Edit2, Trash2, Power, AlertCircle } from 'lucide-react';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';

const BillingPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Check if user is super admin
  const isSuperAdmin = user?.user_metadata?.role === 'super_admin' || 
                       user?.raw_user_meta_data?.role === 'super_admin';

  useEffect(() => {
    loadSubscriptionTiers();
  }, []);

  const loadSubscriptionTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const tiers = isSuperAdmin 
        ? await subscriptionService?.getAllTiers()
        : await subscriptionService?.getActiveTiers();
      setSubscriptionTiers(tiers || []);
    } catch (err) {
      console.error('Failed to load subscription tiers:', err);
      setError(err?.message || 'Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setIsModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleSavePlan = async (planData) => {
    try {
      if (selectedPlan) {
        // Update existing plan
        await subscriptionService?.updateTier(selectedPlan?.id, planData);
      } else {
        // Create new plan
        await subscriptionService?.createTier(planData);
      }
      await loadSubscriptionTiers();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Save plan error:', err);
      throw err;
    }
  };

  const handleToggleStatus = async (planId, currentStatus) => {
    try {
      await subscriptionService?.toggleTierStatus(planId, !currentStatus);
      await loadSubscriptionTiers();
    } catch (err) {
      console.error('Toggle status error:', err);
      setError(err?.message || 'Failed to update plan status');
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await subscriptionService?.deleteTier(planId);
      await loadSubscriptionTiers();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete plan error:', err);
      setError(err?.message || 'Failed to delete plan');
    }
  };

  const currentPlanId = 'professional';

  const usageMetrics = [
    {
      name: 'Contacts',
      icon: 'Users',
      used: 7500,
      limit: 10000,
      type: 'count'
    },
    {
      name: 'Deals',
      icon: 'Target',
      used: 245,
      limit: 500,
      type: 'count'
    },
    {
      name: 'Storage',
      icon: 'HardDrive',
      used: 8.5 * 1024 * 1024 * 1024,
      limit: 10 * 1024 * 1024 * 1024,
      type: 'storage'
    },
    {
      name: 'API Calls',
      icon: 'Zap',
      used: 8750,
      limit: 10000,
      type: 'count'
    }
  ];

  const invoices = [
    {
      id: 'inv_001',
      number: 'INV-2025-001',
      description: 'Professional Plan - November 2025',
      date: '2025-11-01',
      amount: 29.00,
      status: 'Paid'
    },
    {
      id: 'inv_002',
      number: 'INV-2025-002',
      description: 'Professional Plan - October 2025',
      date: '2025-10-01',
      amount: 29.00,
      status: 'Paid'
    },
    {
      id: 'inv_003',
      number: 'INV-2025-003',
      description: 'Professional Plan - September 2025',
      date: '2025-09-01',
      amount: 29.00,
      status: 'Paid'
    },
    {
      id: 'inv_004',
      number: 'INV-2025-004',
      description: 'Professional Plan - August 2025',
      date: '2025-08-01',
      amount: 29.00,
      status: 'Paid'
    },
    {
      id: 'inv_005',
      number: 'INV-2025-005',
      description: 'Professional Plan - July 2025',
      date: '2025-07-01',
      amount: 29.00,
      status: 'Failed'
    },
    {
      id: 'inv_006',
      number: 'INV-2025-006',
      description: 'Professional Plan - June 2025',
      date: '2025-06-01',
      amount: 29.00,
      status: 'Paid'
    }
  ];

  const paymentMethod = {
    type: 'Visa',
    number: '4532123456789012',
    expiry: '12/27',
    name: 'John Doe'
  };

  const billingAlerts = [
    {
      id: 'alert_1',
      type: 'warning',
      title: 'Usage Limit Warning',
      message: 'You have used 75% of your contact limit. Consider upgrading to avoid service interruption.',
      timestamp: '2025-11-03T06:30:00Z',
      actions: [
        {
          label: 'Upgrade Plan',
          variant: 'default',
          icon: 'ArrowUp',
          onClick: () => console.log('Upgrade clicked')
        },
        {
          label: 'View Usage',
          variant: 'outline',
          icon: 'BarChart3',
          onClick: () => console.log('View usage clicked')
        }
      ]
    },
    {
      id: 'alert_2',
      type: 'info',
      title: 'Payment Reminder',
      message: 'Your next payment of $29.00 is due on December 3, 2025.',
      timestamp: '2025-11-02T10:00:00Z',
      actions: [
        {
          label: 'Update Payment Method',
          variant: 'outline',
          icon: 'CreditCard',
          onClick: () => console.log('Update payment clicked')
        }
      ]
    }
  ];

  const handlePlanUpgrade = (plan) => {
    console.log('Upgrading to plan:', plan?.name);
  };

  const handlePaymentUpdate = (paymentData) => {
    console.log('Updating payment method:', paymentData);
  };

  const handleUpgradeClick = () => {
    console.log('Upgrade to premium clicked');
  };

  const handleAlertDismiss = (alertId) => {
    console.log('Alert dismissed:', alertId);
  };

  return (
    <div className="relative">
      <ComponentIdBadge
        id="pg-billing-main"
        name="Billing Main Page"
        description="Subscription management, usage metrics, and billing information"
      />
      <Helmet>
        <title>Billing & Subscription - LocalProCRM</title>
        <meta name="description" content="Manage your LocalProCRM subscription, view usage metrics, and handle billing information" />
      </Helmet>
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 relative">
          <ComponentIdBadge
            id="hdr-billing-title"
            name="Billing Page Header"
            description="Page title and description"
          />
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
            Billing & Subscription
          </h1>
          <p className="text-muted-foreground">
            Manage your subscription, view usage metrics, and handle billing information
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Billing Alerts */}
        <div className="mb-8">
          <BillingAlerts alerts={billingAlerts} onDismiss={handleAlertDismiss} />
        </div>

        {/* Super Admin Plan Management Section */}
        {isSuperAdmin && (
          <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-blue-900 mb-1">
                  Plan Management
                </h2>
                <p className="text-sm text-blue-700">
                  Create, edit, or deactivate subscription plans. Changes sync to signup page automatically.
                </p>
              </div>
              <button
                onClick={handleCreatePlan}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Plan
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subscriptionTiers?.map((tier) => (
                <div
                  key={tier?.id}
                  className={`bg-white rounded-lg border-2 p-4 ${
                    tier?.is_active ? 'border-blue-200' : 'border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{tier?.name}</h3>
                      <p className="text-2xl font-bold text-blue-600 mt-1">
                        ${tier?.base_price}
                        {tier?.per_user_price > 0 && (
                          <span className="text-sm text-gray-600">
                            {' '}+ ${tier?.per_user_price}/user
                          </span>
                        )}
                        <span className="text-sm text-gray-600">/mo</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleStatus(tier?.id, tier?.is_active)}
                        className={`p-2 rounded hover:bg-gray-100 ${
                          tier?.is_active ? 'text-green-600' : 'text-gray-400'
                        }`}
                        title={tier?.is_active ? 'Active' : 'Inactive'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditPlan(tier)}
                        className="p-2 rounded hover:bg-gray-100 text-blue-600"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(tier?.id)}
                        className="p-2 rounded hover:bg-gray-100 text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {tier?.features?.length > 0 && (
                    <div className="space-y-1">
                      {tier?.features?.slice(0, 3)?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <Icon name="Check" size={14} className="text-green-600" />
                          <span>{feature}</span>
                        </div>
                      ))}
                      {tier?.features?.length > 3 && (
                        <p className="text-xs text-gray-500">
                          +{tier?.features?.length - 3} more features
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      tier?.is_active
                        ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      {tier?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Delete Confirmation */}
            {deleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Plan</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Are you sure you want to delete this plan? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeletePlan(deleteConfirm)}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">
            {/* Current Plan Section */}
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-6">
                {isSuperAdmin ? 'Available Plans' : 'Current Plan'}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-2 gap-6">
                {subscriptionTiers?.filter(plan => !isSuperAdmin ? plan?.is_active : true)?.map((plan) => (
                  <PlanCard
                    key={plan?.id}
                    plan={{
                      id: plan?.id,
                      name: plan?.name,
                      description: `Starting at $${plan?.base_price}/month`,
                      price: plan?.base_price,
                      billing: 'month',
                      features: plan?.features?.map(f => ({ name: f, included: true })) || [],
                    }}
                    isCurrentPlan={plan?.id === currentPlanId}
                    onUpgrade={handlePlanUpgrade}
                  />
                ))}
              </div>
            </div>

            {/* Usage Metrics */}
            <UsageMetrics metrics={usageMetrics} />

            {/* Payment Method */}
            <PaymentMethod 
              paymentMethod={paymentMethod} 
              onUpdate={handlePaymentUpdate} 
            />

            {/* Invoice History */}
            <InvoiceTable invoices={invoices} />
          </div>

          {/* Sidebar Content */}
          <div className="space-y-8">
            {/* Upgrade Card */}
            <UpgradeCard onUpgrade={handleUpgradeClick} />

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-muted rounded-lg transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Download" size={16} className="text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground">Download Invoices</span>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </button>
                
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-muted rounded-lg transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="HelpCircle" size={16} className="text-success" />
                    </div>
                    <span className="text-sm font-medium text-foreground">Billing Support</span>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </button>
                
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-muted rounded-lg transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="Settings" size={16} className="text-warning" />
                    </div>
                    <span className="text-sm font-medium text-foreground">Billing Settings</span>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Billing Summary */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Billing Summary</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Current Plan</span>
                  <span className="text-sm font-medium text-foreground">Professional</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Monthly Cost</span>
                  <span className="text-sm font-medium text-foreground">$29.00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Next Payment</span>
                  <span className="text-sm font-medium text-foreground">Dec 3, 2025</span>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Annual Savings</span>
                    <span className="text-sm font-medium text-success">$58.00</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Switch to annual billing and save 20%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Plan Management Modal */}
      <PlanManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        plan={selectedPlan}
        onSave={handleSavePlan}
      />
    </div>
  );
};

export default BillingPage;